import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import PeopleSavingsByLevel from 'Components/people/PeopleSavingsByLevel';

import { formatFigure, formatSeparateByThousands, formatToFixedLocaleString, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import FormInput from 'CommonComponents/FormInput';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import SectionTile from 'CommonComponents/SectionTile';
import SegmentedControl from 'CommonComponents/SegmentedControl';

import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import ExclamationTriangleIcon from 'Assets/icons/exclamation-triangle.svg';
import FileExportIcon from 'Assets/icons/file-export.svg';

/**
 * The delay scenario — what moving uncommitted starts back would save.
 *
 * Every figure here is produced server-side by the shared engine
 * (`DelayScenarioService`), including the cap default. This component deliberately
 * derives **nothing**: it does not convert units, does not work out
 * `surge_start_date ?? event_start_date`, and does not decide whether modelling is
 * available for the version on screen. Each of those is a rule with an edge case,
 * and a rule implemented twice is a rule that will eventually be implemented
 * differently — which is the disagreement the whole People redesign exists to
 * remove.
 *
 * Two modes:
 *  - **Model a delay** — criteria to value.
 *  - **Reach a saving** — value to criteria, returning the smallest delay that
 *    reaches the target, or the maximum achievable when nothing within the cap
 *    does.
 *
 * The scope is uncommitted roles only and there is **no control to include
 * committed ones** — a committed role's start is not the planner's to move, so
 * offering the switch would be offering a saving that cannot be taken.
 */

const MODE_MODEL = 'model';
const MODE_REACH = 'reach';

const UNIT_DAYS = 'days';
const UNIT_WEEKS = 'weeks';
const UNIT_MONTHS = 'months';

/**
 * Typing "50000" into the savings target fires one request per keystroke, and in
 * Reach mode each one runs a segmented search over every position in scope. The
 * out-of-order responses were already discarded by `requestId`, so this is load and
 * latency rather than correctness — but it is the same fix, and the same
 * `_.debounce`-in-a-ref shape, that `PeopleControlRail` already uses to persist the
 * rail's preferences.
 */
const NUMERIC_INPUT_DEBOUNCE_MS = 400;

const COUNT = (value) => formatSeparateByThousands(value);
const MONEY = (value) => formatToFixedMoneyWithLocale(value);
/* One decimal place through the shared helper rather than a bespoke
 * `toLocaleString('en-US')`, so the panel and the by-level table round a delay the
 * same way. */
const DELAY = (value) => formatToFixedLocaleString(value, 1);

const UNIT_LABELS = {
    [UNIT_DAYS]: () => $t('days'),
    [UNIT_WEEKS]: () => $t('weeks'),
    [UNIT_MONTHS]: () => $t('months'),
};

/** `null`/`undefined` render as an em dash; a genuine 0 renders as 0. */
function present(value, format) {
    return value === null || value === undefined ? '—' : format(value);
}

function ResultTile({ title, value, hint }) {
    return (
        <div className="tw-px-4 tw-py-3 tw-rounded-lg tw-border-2 tw-border-grey">
            <div className="tw-text-sm tw-text-blue-75 tw-font-medium tw-mb-2">{title}</div>
            <div className="tw-text-2.5xl tw-text-charcoal tw-font-semibold">{value}</div>
            {!!hint && <div className="tw-mt-1 tw-text-xs tw-text-charcoal-50">{hint}</div>}
        </div>
    );
}

/**
 * A banded notice, using the reconciliation strip's own vocabulary so the two read
 * as one family.
 *
 * `alert` uses red, which the design reserves for alerts — and roles that would
 * start after their own end date are exactly that: with the duration floor dropped
 * from the design, this warning is the **only** protection against a short role
 * being deleted rather than delayed.
 */
function Notice({ tone = 'info', title, children }) {
    const alert = tone === 'alert';
    const Icon = alert ? ExclamationTriangleIcon : CircleInfoIcon;

    return (
        <div
            className={classNames(
                'tw-flex tw-items-start tw-gap-x-3 tw-px-4 tw-py-3 tw-rounded-lg tw-border-2',
                alert ? 'tw-bg-red-10 tw-border-red-50' : 'tw-bg-blue-10 tw-border-blue-50',
            )}
            role={alert ? 'alert' : 'status'}
        >
            <Icon className={classNames('tw-w-4 tw-h-4 tw-shrink-0 tw-mt-0.5', alert ? 'tw-text-red' : 'tw-text-blue')} />
            <div className="tw-min-w-0">
                <div className="tw-text-sm tw-font-semibold tw-text-charcoal">{title}</div>
                {!!children && <div className="tw-text-xs tw-text-charcoal-75 tw-mt-0.5">{children}</div>}
            </div>
        </div>
    );
}

/**
 * The roles the integrity warning is about, listed rather than merely counted.
 *
 * Money keys are absent from these rows for a user without pay-rate visibility, so
 * the rate column is dropped by inspecting what a row carries.
 */
function IntegrityBreachList({ breaches, unit }) {
    const showRate = breaches.length > 0 && 'rate' in breaches[0];

    return (
        <div className="inbud-table_wrapper tw-mt-3">
            <table className="inbud-table tw-table-fixed">
                <thead>
                    <tr>
                        <th className="tw-w-56">{$t('Role')}</th>
                        <th className="tw-w-40">{$t('Level')}</th>
                        <th className="tw-w-32">{$t('Delay applied ({{unit}})', { unit })}</th>
                        <th className="tw-w-32">{$t('New start')}</th>
                        <th className="tw-w-32">{$t('Own end date')}</th>
                        {showRate && <th className="tw-w-32">{$t('Rate')}</th>}
                    </tr>
                </thead>
                <tbody>
                    {breaches.map((breach) => (
                        <tr key={breach.position_id}>
                            <td>
                                <div className="tw-truncate">{breach.name}</div>
                            </td>
                            <td>{breach.level || '—'}</td>
                            <td className="inbud-numeric-cell">{formatFigure(breach.effective_delay_in_unit, formatToFixedLocaleString, { precision: 1 })}</td>
                            <td className="inbud-date-cell">{breach.new_start_date || '—'}</td>
                            <td className="inbud-date-cell">{breach.end_date || '—'}</td>
                            {showRate && <td className="inbud-numeric-cell">{formatFigure(breach.rate, formatToFixedMoneyWithLocale)}</td>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const GROUP_BY_DEPARTMENT = 'department';
const GROUP_BY_LEVEL = 'level';

export default function PeopleDelayScenario({ url, exportUrl, controls }) {
    const [mode, setMode] = useState(MODE_MODEL);
    const [unit, setUnit] = useState(UNIT_WEEKS);
    // Two copies of each numeric input on purpose: the raw one drives the field so
    // typing stays responsive, and the settled one drives the request.
    const [delay, setDelay] = useState('8');
    const [target, setTarget] = useState('');
    const [settled, setSettled] = useState({ delay: '8', target: '' });
    // `null` until the first response arrives: the cap default is resolved on the
    // server, so the panel asks for it rather than guessing it.
    const [cap, setCap] = useState(null);
    const [capTouched, setCapTouched] = useState(false);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    // Only offered when the payload carries `by_department` (event level); both
    // groupings arrive in one response, so switching does not refetch.
    const [grouping, setGrouping] = useState(GROUP_BY_DEPARTMENT);
    const [failed, setFailed] = useState(false);

    const requestId = useRef(0);

    const settle = useRef(_.debounce((patch) => setSettled((current) => ({ ...current, ...patch })), NUMERIC_INPUT_DEBOUNCE_MS));

    useEffect(() => {
        return () => settle.current.cancel();
    }, []);

    // A primitive, not an expression inside the memo. The cap default arriving from
    // the server calls `setCap()` while `capTouched` is still false, which changes
    // `cap` without changing the QUERY — and a `cap` dependency would hand the memo
    // a new object with identical contents, firing a second identical request and
    // an opacity flash on first paint.
    const capParam = capTouched && cap ? cap : null;

    const params = useMemo(() => {
        const query = {
            mode,
            unit,
            // `position_types` without the `[]` suffix — axios appends it itself, so
            // `'position_types[]'` would double up into `position_types[][]=P`.
            position_types: controls.position_types,
        };

        if (mode === MODE_REACH) {
            query.target = Number(settled.target) || 0;
        } else {
            query.delay = Number(settled.delay) || 0;
        }

        // Only sent once the user has actually chosen one. Until then the server
        // resolves the cap itself, which is what lets the panel learn the default
        // (`surge_start_date ?? event_start_date`) without re-deriving it — and
        // means adopting that default for the date picker does not trigger a second
        // request for the same answer.
        if (capParam) {
            query.cap = capParam;
        }

        return query;
    }, [mode, unit, settled.delay, settled.target, capParam, controls.position_types]);

    useEffect(() => {
        const id = ++requestId.current;

        setLoading(true);
        setFailed(false);

        axios
            .get(url, { params })
            .then((response) => {
                if (id !== requestId.current) {
                    return;
                }

                setPayload(response.data);
                setLoading(false);

                // Adopt the server's cap default for the date picker, but never
                // over a cap the user has chosen — re-adopting it would silently
                // undo their choice on the next refetch.
                if (!capTouched && response.data.default_cap_date) {
                    setCap(response.data.default_cap_date);
                }
            })
            .catch(() => {
                if (id !== requestId.current) {
                    return;
                }

                setFailed(true);
                setLoading(false);
            });
    }, [url, params, capTouched]);

    if (failed) {
        return <p className="tw-text-sm tw-text-red">{$t('The delay scenario could not be loaded. Please try again.')}</p>;
    }

    if (loading && !payload) {
        return (
            <div className="tw-flex tw-justify-center tw-py-10">
                <LoadingSpinner />
            </div>
        );
    }

    if (!payload) {
        return null;
    }

    const unitLabel = UNIT_LABELS[unit]();
    const breaches = payload.integrity_breaches ?? [];
    const hasDepartmentGrouping = Array.isArray(payload.by_department);
    const byDepartment = hasDepartmentGrouping && grouping === GROUP_BY_DEPARTMENT;
    const exportQuery = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((entry) => exportQuery.append(`${key}[]`, entry));
        } else if (value !== null && value !== undefined) {
            exportQuery.append(key, value);
        }
    });

    return (
        <div className={classNames('tw-space-y-4', { 'tw-opacity-60': loading })}>
            <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-x-5 tw-gap-y-3">
                <div>
                    <div className="tw-text-xs tw-font-medium tw-text-charcoal-50 tw-mb-1.5">{$t('Mode')}</div>
                    <SegmentedControl
                        options={[
                            { value: MODE_MODEL, label: $t('Model a delay') },
                            { value: MODE_REACH, label: $t('Reach a saving') },
                        ]}
                        value={mode}
                        onChange={setMode}
                    />
                </div>

                <div>
                    <div className="tw-text-xs tw-font-medium tw-text-charcoal-50 tw-mb-1.5">{$t('Delay unit')}</div>
                    <SegmentedControl
                        options={[
                            { value: UNIT_DAYS, label: $t('Days') },
                            { value: UNIT_WEEKS, label: $t('Weeks') },
                            { value: UNIT_MONTHS, label: $t('Months') },
                        ]}
                        value={unit}
                        onChange={setUnit}
                    />
                </div>

                {mode === MODE_MODEL ? (
                    <div className="tw-w-32">
                        <FormInput
                            label={$t('Delay ({{unit}})', { unit: unitLabel })}
                            name="delay"
                            type="number"
                            min="0"
                            value={delay}
                            onChange={(event) => {
                                setDelay(event.target.value);
                                settle.current({ delay: event.target.value });
                            }}
                        />
                    </div>
                ) : (
                    <div className="tw-w-40">
                        <FormInput
                            label={$t('Savings target')}
                            name="target"
                            type="number"
                            min="0"
                            value={target}
                            onChange={(event) => {
                                setTarget(event.target.value);
                                settle.current({ target: event.target.value });
                            }}
                        />
                    </div>
                )}

                {/* Highlighted, per @dave: the default comes from the event start
                    date unless a surge date has been recorded, and the busy period
                    usually begins earlier — so the pre-filled value is a starting
                    point to review, not an answer.

                    The highlight is stated in words as well as in colour. @dave
                    asked for it "in a way for the user to know to update it", and a
                    blue border on its own says only "this field is different".

                    Width-capped so the two hint lines wrap inside the box rather
                    than stretching it across the control rail and pushing Export
                    onto its own row. */}
                <div className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-blue-10 tw-border-2 tw-border-blue-50 tw-max-w-xs">
                    <div className="tw-text-xs tw-font-medium tw-text-charcoal-75 tw-mb-1">{$t('Do not move any start beyond')}</div>
                    <DatePickerField
                        name="cap"
                        className="text-input tw-border-0"
                        value={cap}
                        valueFormat="yyyy-MM-dd"
                        onChange={(name, value) => {
                            // Clearing the field reverts to "untouched" rather than
                            // holding an empty picker: the server always applies its
                            // own default when no cap is sent, so treating a cleared
                            // field as a choice would leave the figures computed
                            // against a date the panel was no longer showing.
                            setCapTouched(!!value);
                            setCap(value);
                        }}
                    />
                    <div className="tw-mt-1 tw-text-xs tw-text-charcoal-75">
                        {capTouched
                            ? $t('Your own cut-off date. Clear it to go back to the default.')
                            : $t('Pre-filled from the surge start date — check it before relying on the saving.')}
                    </div>
                    {/* A cut-off set here is scenario-local and never persisted, so
                        say so and name where the stored one lives. Without this a
                        user can change the date, export the scenario, and reasonably
                        believe they had recorded the assumption — the export carries
                        the cap they chose, which makes the belief look confirmed.

                        Persisting from here would be the wrong fix: one person
                        trying a cut-off would move every other user's default and
                        shift the peak-excluding-surge figures (IB-934) with it. */}
                    <div className="tw-mt-1 tw-text-xs tw-text-charcoal-50">
                        {$t('Changing it here affects this scenario only. The saved default is the surge start date on the Workforce Assumptions tab.')}
                    </div>
                </div>

                <Button variant="secondary" small as="a" href={`${exportUrl}?${exportQuery.toString()}`}>
                    <FileExportIcon className="tw-w-3.5 tw-h-3.5 tw-me-2" />
                    {$t('Export')}
                </Button>
            </div>

            <p className="tw-text-xs tw-text-charcoal-50 tw-mb-0">
                {$t('{{count}} uncommitted roles are in scope. Committed roles cannot be delayed and are never included.', {
                    count: COUNT(payload.scope_count),
                })}
            </p>

            {payload.cap_missing && (
                <Notice title={$t('No cut-off date to work from')}>
                    {$t(
                        'This event records neither a surge start date nor an event start date, so there is no date to pre-fill. Choose a cut-off above to model a delay.',
                    )}
                </Notice>
            )}

            {!payload.modelled_available && (
                <Notice title={$t('Modelled savings are only available on the evolving version')}>
                    {$t(
                        'You are viewing an approved or prior version. Savings already realised are still shown below; a delay cannot be modelled against a version nobody can change.',
                    )}
                </Notice>
            )}

            {payload.modelled_available && !payload.cap_missing && (
                <>
                    <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-x-5 tw-gap-y-3">
                        <ResultTile title={$t('Saving')} value={present(payload.saving, MONEY)} />
                        <ResultTile title={$t('Positions impacted')} value={present(payload.positions_impacted, COUNT)} />
                        {/* Both delay figures are reported in the unit the user
                            picked — the criterion is that the selector governs every
                            one of them, and the server sends each already converted
                            so this component never re-derives the divisor. */}
                        <ResultTile title={$t('Average effective delay')} value={present(payload.average_effective_delay_in_unit, DELAY)} hint={unitLabel} />
                        <ResultTile
                            title={mode === MODE_REACH ? $t('Delay required') : $t('Delay applied')}
                            value={present(payload.requested_delay_in_unit, DELAY)}
                            hint={unitLabel}
                        />
                    </div>

                    {payload.target_reachable === false && (
                        <Notice title={$t('That target cannot be reached within this cut-off date')}>
                            {$t(
                                'The figures above are the most this cut-off can save. Move the cut-off later to free up more headroom, or accept the smaller saving.',
                            )}
                        </Notice>
                    )}

                    {payload.estimated_position_count > 0 && (
                        <Notice title={$t('{{count}} of these figures are estimates', { count: COUNT(payload.estimated_position_count) })}>
                            {$t(
                                'Consultants are budgeted as a quantity times a price rather than by the month, so their share of a delay is their fee spread over the booking. Roles with no pay rate recorded are estimated the same way.',
                            )}
                        </Notice>
                    )}

                    {payload.integrity_breach_count > 0 && (
                        <div>
                            <Notice
                                tone="alert"
                                title={$t('{{count}} roles would start after their own end date', { count: COUNT(payload.integrity_breach_count) })}
                            >
                                {$t(
                                    'Bring the cut-off date forward to protect them. They are modelled at no cost and no cover, and are still counted everywhere below — but at this delay they would not happen at all.',
                                )}
                            </Notice>
                            {breaches.length > 0 && <IntegrityBreachList breaches={breaches} unit={unitLabel} />}
                        </div>
                    )}
                </>
            )}

            <SectionTile
                flat
                header={$t('Savings from delayed hires')}
                subHeader={
                    byDepartment
                        ? 'By department, with what has already been saved beside what a delay would save'
                        : $t('By level, with what has already been saved beside what a delay would save')
                }
            >
                {hasDepartmentGrouping && (
                    <div className="tw-flex tw-justify-end tw-mb-3">
                        <SegmentedControl
                            variant="track"
                            options={[
                                { value: GROUP_BY_DEPARTMENT, label: 'By department' },
                                { value: GROUP_BY_LEVEL, label: 'By level' },
                            ]}
                            value={grouping}
                            onChange={setGrouping}
                        />
                    </div>
                )}
                {byDepartment ? (
                    <PeopleSavingsByLevel
                        rows={payload.by_department}
                        unit={unitLabel}
                        labelHeader="Department"
                        emptyMessage="No positions to break down by department yet."
                    />
                ) : (
                    <PeopleSavingsByLevel rows={payload.by_child_level ?? []} unit={unitLabel} />
                )}
            </SectionTile>
        </div>
    );
}
